import {
	A, connectedComponent, List, loadFacilities,
	LoadingIndicator, SortableTable
} from 'gwx-voyager-components';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import React, { Component } from 'react';
import './sendingFacilityList.scss';

/**
 * Component representing a list of Sending Facilities
 *
 * @class SendingFacilityList
 * @extends {Component}
 */
class SendingFacilityList extends Component {
	constructor(props) {
		super(props);
		this.discardConfirmation = this.discardConfirmation.bind(this);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleSort = this.handleSort.bind(this);
		this.onClickRefresh = this.onClickRefresh.bind(this);
		this.state = {
			confirmation: null
		};
	}

	componentDidMount() {
		if (this.props.fetchSendingFacilities) {
			const query = {
				...this.props.query,
				communityId: this.props.communityId
			};
			this.props.fetchSendingFacilities(query);
		}
	}

	shouldComponentUpdate(nextProps) {
		if (JSON.stringify(nextProps.query) !== JSON.stringify(this.props.query)) {
			const query = {
				...nextProps.query,
				communityId: this.props.communityId
			};
			this.props.fetchSendingFacilities(query);
			return false;
		}
		return true;
	}

	onClickRefresh() {
		const query = {
			...this.props.query,
			communityId: this.props.communityId
		};
		this.props.fetchSendingFacilities(query);
	}

	handleInputChange(e) {
		let value = e;
		if (!this.state.filter.selector) {
			value = value ? value.target && value.target.value : '';
		}

		this.setState((prevState) => ({
			filter: { ...prevState.filter, value }
		}));
	}

	handleSort(sortBy) {
		if (sortBy && sortBy.length > 0 && this.props.sort && this.props.sort.length > 0) {
			const { sort } = this.props;
			const desc = sort[0].direction === 'DESC';

			if (sortBy[0].id !== sort[0].property || sortBy[0].desc !== desc) {
				const newSort = {
					direction: sortBy[0].desc ? 'DESC' : 'ASC',
					property: sortBy[0].id,
					page: 0
				};

				const query = {
					...this.props.query,
					...newSort
				};
				this.props.onSort(query);
			}
		}
	}

	discardConfirmation() {
		this.setState({ confirmation: null });
	}

	render() {
		const rows = this.props.sendingFacilities ? this.props.sendingFacilities.map((f) => ({
			id: f.id,
			displayName: (
				<A href={`/${this.props.communityId}/administration/edit/${f.id}?${stringify(this.props.query)}`} reactRouter>
					{f.displayName}
				</A>
			),
			inReplyTo: f.inReplyTo || '--',
			internalDomains: (f.sendingFacilityInternalDomains && f.sendingFacilityInternalDomains.filter((a) => a !== null && a !== '').map((b) => b.domain).join(', ')) || '--'

		})) : [];
		const listProps = {
			rows: [],
			refresh: {
				id: 'refresh-settings-button',
				title: 'Refresh Settings',
				onClick: this.onClickRefresh
			},
			updateQueryParam: (key, value) => this.props.history.push(`?${stringify({
				...this.props.query,
				[key]: value
			})}`),
			isFetching: false,
			pagination: {
				nextPage: this.props.nextPage,
				previousPage: this.props.previousPage,
				startCount: this.props.startCount,
				endCount: this.props.endCount,
				total: this.props.totalCount
			}

		};
		const headers = [
			{
				Header: 'Display Name',
				accessor: 'displayName'
			},
			{
				Header: 'Reply-To',
				accessor: 'inReplyTo'
			},
			{
				Header: 'Internal Domains',
				accessor: 'internalDomains',
				disableSortBy: true
			}
		];

		const initialState = {
			sortBy: [
				{
					id: (this.props.sort && this.props.sort[0] && this.props.sort[0].property) || 'displayName',
					desc: (this.props.sort && this.props.sort[0] && this.props.sort[0].direction) ? this.props.sort[0].direction === 'DESC' : false
				}
			]
		};

		const gridProps = {
			columns: headers,
			data: rows,
			manualSortBy: true,
			disableMultiSort: true,
			disableSortRemove: true,
			disableMultiRemove: true,
			onSort: (sortBy) => this.handleSort(sortBy),
			initialState
		};

		return (
			<div>
				<List {...listProps} />
				{this.props.isFetching ? <LoadingIndicator /> : <SortableTable {...gridProps} />}
				{this.state.confirmation}
			</div>
		);
	}
}

SendingFacilityList.defaultProps = {
	sort: []
};

SendingFacilityList.propTypes = {
	fetchSendingFacilities: PropTypes.func.isRequired,
	isFetching: PropTypes.bool.isRequired,
	sendingFacilities: PropTypes.arrayOf(PropTypes.shape({
		displayName: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		inReplyTo: PropTypes.string.isRequired
	})).isRequired,
	onSort: PropTypes.func.isRequired,
	sort: PropTypes.arrayOf(PropTypes.shape({
		direction: PropTypes.string,
		property: PropTypes.string
	}))
};
/**
 * Maps the store's state to a set of props to be passed
 * to the SendingFacilityList component.
 *
 * @param {object} state
 * 		The current state of the store.
 */
const mapStateToProps = (state, props) => {
	const {
		entities: { sendingFacilities }
	} = state;

	const {
		match: { params: { id: communityId } }
	} = props;

	return {
		communityId,
		context: `${communityId}/administration`,
		isFetching: sendingFacilities.isFetching,
		sendingFacilities: sendingFacilities.ids.map(
			(id) => sendingFacilities.results[id]
		),
		component: 'sendingFacilities'
	};
};

/**
 * Creates a set of functions that utilize the dispatcher to dispatch actions.
 * These are the Action Creators for the SendingFacilityList component.
 *
 * @param {function} dispatch
 * 		The redux dispatcher
 * @para {object} props
 * 		The props that will be passed to the SendingFacilityList component.
 */
const mapDispatchToProps = (dispatch, props) => ({
	/**
	 * Makes a fetch request for the list of sending facilities.
	 */
	fetchSendingFacilities: (query) => dispatch(loadFacilities(query)),
	/**
	 * Makes the query changes that will request sorted results
	 */
	onSort: (query) => props.history.push(`/${props.match.params.id}/administration?${stringify(query)}`)
});

const SendingFacilityListContainer = connectedComponent(SendingFacilityList, mapStateToProps, mapDispatchToProps, true, true);

export {
	SendingFacilityListContainer as default,
	mapStateToProps,
	SendingFacilityList
};
